import _ from "lodash";
import { DateUtils } from "../utils/DateUtils";
import moment from "moment";
import { Contact } from "./Contact";
import { Employe } from "./Employe";
import { ModificationHistory } from "./ModificationHistory";
import { JournalEntry } from "./JournalEntry";
import { Coord } from "./Coord";
import { Routes } from "../utils/Routes";
import { Email } from "./Email";

// Class
export class Intervention {
    id?: number;
    private _client?: Contact;
    client_id?: number;
    date_prevue_intervention?: string;
    date_reelle_intervention?: string;
    duree_prevue?: number;
    duree_reelle?: number;
    employes: Array<Employe> = [];
    etat: Intervention.EEtat = Intervention.EEtat.Pending;
    frequence: Intervention.EFrequence = Intervention.EFrequence.Ponctuel;
    facturation: Intervention.Facturation | null = null;
    heure_reelle_debut_intervention?: string;
    heure_reelle_fin_intervention?: string;
    horaire_debut?: string;
    horaire_fin?: string;
    journal_entry?: JournalEntry;
    notification?: Intervention.Notification;
    notifications: Array<Intervention.Notification> = [];
    notification_bon_de_passage?: boolean;
    severity?: number;
    titre?: string;
    is_planned?: boolean;
    photos: Array<Object> = [];
    interventions_secondaires: Array<Intervention.InterventionSecondaire> = [];
    history: Array<ModificationHistory> = [];
    recurrence_intervention_id?: number;
    copied_intervention_id?: number;
    is_as_planned?: boolean;
    _facturation_to_delete: Intervention.Facturation | null = null;
    created_at?: string;
    hash?: string;
    coord_debut_intervention?: Coord;
    coord_fin_intervention?: Coord;
    distance_pointage_debut_intervention?: number;
    distance_pointage_fin_intervention?: number;
    maps_image_url?: string;
    anomalies: Array<Intervention.EAnomaly> = [];
    to_notify: boolean = false;
    controle: boolean = false;
    mise_en_place: boolean = false;
    latest_notified_etat?: Intervention.EEtat;

    constructor(data: any) {
        var self = this;
        _.assign(
            this,
            _.omit(data, [
                "client",
                "employes",
                "interventions_secondaires",
                "history",
                "facturation",
                "journal_entry",
                "notification",
                "notifications",
            ])
        );
        if (data.client) {
            self._client = new Contact(data.client);
        }
        if (data.facturation) {
            self.facturation = new Intervention.Facturation(data.facturation);
        }
        if (data.journal_entry) {
            self.journal_entry = new JournalEntry(data.journal_entry);
        }
        if (data.notification) {
            self.notification = new Intervention.Notification(
                data.notification
            );
        }
        _.forEach(data.notifications, function (notification) {
            self.notifications.push(
                new Intervention.Notification(notification)
            );
        });
        _.forEach(data.employes, function (employe) {
            self.employes.push(new Employe(employe));
        });
        _.forEach(
            data.interventions_secondaires,
            function (intervention_secondaire) {
                self.interventions_secondaires.push(
                    new Intervention.InterventionSecondaire(
                        intervention_secondaire
                    )
                );
            }
        );
        _.forEach(data.history, function (historyLine) {
            self.history.push(new ModificationHistory(historyLine));
        });
    }

    //
    get date_prevue_str(): string {
        return DateUtils.sqlToDateStr(this.date_prevue_intervention);
    }

    set date_prevue_str(date: string) {
        var newDate = DateUtils.dateStrToSql(date);

        if (!_.isEmpty(newDate)) {
            this.date_prevue_intervention = newDate;
        }
    }

    //
    get date_reelle_intervention_str(): string {
        return DateUtils.sqlToDateStr(this.date_reelle_intervention);
    }

    set date_reelle_intervention_str(date: string) {
        var newDate = DateUtils.dateStrToSql(date);

        if (!_.isEmpty(newDate)) {
            this.date_reelle_intervention = newDate;
        }
    }

    //
    get frequence_name(): string {
        if (!_.isUndefined(this.frequence)) {
            return Intervention.Frequence[this.frequence].name;
        } else {
            return "";
        }
    }

    get frequence_shortname(): string {
        if (!_.isUndefined(this.frequence)) {
            return Intervention.Frequence[this.frequence].shortname;
        } else {
            return "";
        }
    }

    //
    get isPonctuel(): boolean {
        return this.frequence == Intervention.EFrequence.Ponctuel;
    }

    get etat_display(): Intervention.IEtat {
        if (this.isLate) {
            return Intervention.Etat[Intervention.EEtat.Late];
        } else {
            return Intervention.Etat[this.etat];
        }
    }

    get dernier_etat_notifie(): Intervention.IEtat {
        return Intervention.Etat[this.latest_notified_etat];
    }

    get isLate(): boolean {
        let now = moment();
        let startDate = moment(
            this.date_prevue_intervention + " " + this.horaire_debut,
            "YYYY-MM-DD HH[h]mm"
        );
        return this.etat == Intervention.EEtat.Pending && now > startDate;
    }

    get canDelete(): boolean {
        // Suppression interdite pour les interventions passées
        if (!this.date_prevue_intervention) {
            return true;
        }
        let now = moment();
        let startDate = moment(this.date_prevue_intervention, "YYYY-MM-DD");
        return now.format("YYYY-MM-DD") <= startDate.format("YYYY-MM-DD");
    }

    //
    get heure_reelle_debut_intervention_str(): string {
        var res = "";
        let date = moment(this.heure_reelle_debut_intervention);
        if (date.isValid()) {
            res = date.format("HH[h]mm");
        }
        return res;
    }

    get heure_reelle_fin_intervention_str(): string {
        var res = "";
        let date = moment(this.heure_reelle_fin_intervention);
        if (date.isValid()) {
            res = date.format("HH[h]mm");
        }
        return res;
    }

    get periode_intervention_str(): string {
        return (
            this.heure_reelle_debut_intervention_str +
            " - " +
            this.heure_reelle_fin_intervention_str
        );
    }

    //
    get client(): Contact | undefined {
        return this._client;
    }

    set client(value: Contact | undefined) {
        if (value) {
            this._client = value;
            this.client_id = value.id;
        }
    }

    //
    get sharedPhotos(): Array<Object> {
        return _.filter(this.photos, function (photo) {
            // @ts-ignore
            return photo.shared == true;
        });
    }

    //
    get nonFacturation(): boolean {
        return !this.facturation;
    }

    set nonFacturation(value: boolean) {
        if (value) {
            this._facturation_to_delete = this.facturation;
            this.facturation = null;
        } else {
            if (this._facturation_to_delete) {
                this.facturation = this._facturation_to_delete;
            } else {
                this.facturation = new Intervention.Facturation({});
            }
            this._facturation_to_delete = null;
        }
    }

    //
    get employes_ids(): Array<number> {
        // @ts-ignore
        return _.map(this.employes, "id");
    }

    //
    // Bons de passage ramenés au format des mails de la modale d'historique
    get displayableMails(): Array<any> {
        return _.map(
            this.notifications,
            function (notification: Intervention.Notification) {
                return {
                    type_id: Email.EEmailTypes.outgoing,
                    subject:
                        "Bon de passage " + notification.notification_number,
                    recipient: notification.recipient,
                    content: notification.content,
                    created_at: notification.created_at,
                    attachments: notification.attachments,
                };
            }
        );
    }

    get creation_date_str(): string {
        if (this.created_at) {
            return DateUtils.sqlToDateStr(this.created_at);
        } else {
            return "";
        }
    }

    // Utils
    updateHoraireFin() {
        if (this.horaire_debut && this.duree_prevue) {
            let date = moment(this.horaire_debut, "HH[h]mm");
            date.add(this.duree_prevue, "h");
            this.horaire_fin = date.format("HH[h]mm");
        }
    }

    serialize(): Intervention.IIntervention {
        var res = {
            id: this.id,
            client_id: this.client_id,
            date_prevue_intervention: this.date_prevue_intervention,
            date_reelle_intervention: this.date_reelle_intervention,
            duree_prevue: this.duree_prevue,
            duree_reelle: this.duree_reelle,
            employes_ids: this.employes_ids,
            etat: this.etat,
            to_notify: this.to_notify,
            controle: this.controle,
            mise_en_place: this.mise_en_place,
            frequence: this.frequence,
            heure_reelle_debut_intervention:
                this.heure_reelle_debut_intervention,
            heure_reelle_fin_intervention: this.heure_reelle_fin_intervention,
            horaire_debut: this.horaire_debut,
            horaire_fin: this.horaire_fin,
            journal_entry: this.journal_entry,
            notification_bon_de_passage: this.notification_bon_de_passage,
            severity: this.severity,
            titre: this.titre,
        };

        return res;
    }

    get link(): string {
        return Routes.intervention_list_filter + "id=" + this.id;
    }

    get statusHtml(): string {
        let circleImg =
            '<img src="/img/interface/mailing/circle.png" style="min-width: 7px; min-height: 7px;" width="7" height="7" />';

        let barImg =
            "<img src='/img/interface/mailing/bar.png' style='min-width: 3px; min-height: 25px;' width='3' height='25' />";

        let tagOpen =
            "<tr><td align='center'>" +
            circleImg +
            '</td><td style="padding-left: 10px;"><i>';
        let tagClose = "</i></td></tr>";
        let barRow = "<tr><td align='center'>" + barImg + "</td><td></td></tr>";

        let res = "";

        res += "<h4>" + this.titre + "</h4>";

        res += "<table>";

        if (!this.isPonctuel && !this.date_prevue_intervention) {
            res += tagOpen;
            res += "Intervention en attente de programmation";
            res += tagClose;
        }
        if (this.isPonctuel) {
            res += tagOpen;
            res += "Demande prise en compte";
            if (this.date_prevue_intervention) {
                res += barRow;
            } else {
                res +=
                    ",<br />nous vous tiendrons prochainement informé de la date d'intervention";
            }
            res += tagClose;
        }
        if (this.date_prevue_intervention) {
            res += tagOpen;
            res += "Intervention programmée le " + this.date_prevue_str;
            if (this.etat != Intervention.EEtat.Pending) {
                res += barRow;
            }
            res += tagClose;
        }
        if (this.etat == Intervention.EEtat.Ongoing) {
            res += tagOpen;
            res += "Intervention en cours";
            if (this.sharedPhotos.length > 0) {
                res += ' (<a href="';
                res +=
                    window.location.origin +
                    "/#/intervention/" +
                    this.hash +
                    "/photos";
                res += '">';
                res += "voir les photos";
                res += "</a>)";
            }
            res += tagClose;
        }
        if (
            this.etat == Intervention.EEtat.Finished ||
            this.etat == Intervention.EEtat.Archived
        ) {
            res += tagOpen;
            res +=
                "Intervention terminée" +
                (this.date_reelle_intervention
                    ? " le " + this.date_reelle_intervention_str
                    : "");
            if (this.sharedPhotos.length > 0) {
                res += ' (<a href="';
                res +=
                    window.location.origin +
                    "/#/intervention/" +
                    this.hash +
                    "/photos";
                res += '">';
                res += "voir les photos";
                res += "</a>)";
            }
            res += tagClose;
        }

        res += "</table>";

        return res;
    }
}

// Enums
export namespace Intervention {
    export interface IIntervention {
        id?: number;
        client_id?: number;
        date_prevue_intervention?: string;
        date_reelle_intervention?: string;
        duree_prevue?: number;
        duree_reelle?: number;
        employes_ids: Array<number>;
        etat: number;
        to_notify: boolean;
        controle: boolean;
        mise_en_place: boolean;
        frequence?: number;
        heure_reelle_debut_intervention?: string;
        heure_reelle_fin_intervention?: string;
        horaire_debut?: string;
        horaire_fin?: string;
        journal_entry?: object;
        notification_bon_de_passage?: boolean;
        severity?: number;
        titre?: string;
    }

    export interface IFacturation {
        id?: number;
        intervention_id?: number;
        copropriete_id?: number;
        nb_intervenants: number;
        nb_heures_par_intervenant: number;
        taux_horaire: number;
        complement: number;
        remise: number;
        taux_tva: number;
        montant_ht: number;
        montant_ttc: number;
        date_facturation: string;
        status: EFacturationStatus;
    }

    // Fréquence
    export interface IFrequence {
        id: EFrequence;
        name: string;
        shortname: string;
    }

    export enum EFrequence {
        Ponctuel = 0,
        DuLundiAuSamedi = 1,
        DuLundiAuVendredi = 2,
        Hebdomadaire = 3,
        ToutesLesDeuxSemaines = 10,
        Bimensuel = 4,
        Mensuel = 5,
        Bimestriel = 6,
        Trimestriel = 7,
        Semestriel = 8,
        Annuel = 9,
        None = 100,
    }

    export const Frequence: { [index: number]: IFrequence } = {
        [EFrequence.Ponctuel]: {
            id: EFrequence.Ponctuel,
            name: "Ponctuel",
            shortname: "Ponct.",
        },
        [EFrequence.DuLundiAuSamedi]: {
            id: EFrequence.DuLundiAuSamedi,
            name: "Tous les jours (du lundi au samedi)",
            shortname: "Jour.",
        },
        [EFrequence.DuLundiAuVendredi]: {
            id: EFrequence.DuLundiAuVendredi,
            name: "Tous les jours (du lundi au vendredi)",
            shortname: "Jour.",
        },
        [EFrequence.Hebdomadaire]: {
            id: EFrequence.Hebdomadaire,
            name: "Hebdomadaire",
            shortname: "Hebdo.",
        },
        [EFrequence.ToutesLesDeuxSemaines]: {
            id: EFrequence.ToutesLesDeuxSemaines,
            name: "Toutes les deux semaines",
            shortname: "Quinz.",
        },
        [EFrequence.Bimensuel]: {
            id: EFrequence.Bimensuel,
            name: "Bimensuel",
            shortname: "Bimens.",
        },
        [EFrequence.Mensuel]: {
            id: EFrequence.Mensuel,
            name: "Mensuel",
            shortname: "Mens.",
        },
        [EFrequence.Bimestriel]: {
            id: EFrequence.Bimestriel,
            name: "Bimestriel",
            shortname: "Bim.",
        },
        [EFrequence.Trimestriel]: {
            id: EFrequence.Trimestriel,
            name: "Trimestriel",
            shortname: "Trim.",
        },
        [EFrequence.Semestriel]: {
            id: EFrequence.Semestriel,
            name: "Semestriel",
            shortname: "Sem.",
        },
        [EFrequence.Annuel]: {
            id: EFrequence.Annuel,
            name: "Annuel",
            shortname: "Annuel",
        },
        [EFrequence.None]: {
            id: EFrequence.None,
            name: "Pas d'intervention",
            shortname: "Pause",
        },
    };

    // État
    export interface IEtat {
        id: EEtat;
        name: string;
        colorClass: string;
        cssClass: string;
    }

    export enum EEtat {
        Pending = 0,
        Ongoing = 1,
        Finished = 2,
        Archived = 3,
        Late = 100,
    }

    export const Etat: { [index: number]: IEtat } = {
        [EEtat.Pending]: {
            id: EEtat.Pending,
            name: "En attente",
            colorClass: "info",
            cssClass: "status-pending",
        },
        [EEtat.Ongoing]: {
            id: EEtat.Ongoing,
            name: "En cours",
            colorClass: "warning",
            cssClass: "status-ongoing",
        },
        [EEtat.Finished]: {
            id: EEtat.Finished,
            name: "Terminée",
            colorClass: "success",
            cssClass: "status-finished",
        },
        [EEtat.Archived]: {
            id: EEtat.Archived,
            name: "Archivée",
            colorClass: "success",
            cssClass: "status-finished",
        },
        [EEtat.Late]: {
            id: EEtat.Late,
            name: "En retard",
            colorClass: "danger",
            cssClass: "status-late",
        },
    };

    // Anomalies
    export interface IAnomaly {
        id: EAnomaly;
        name: string;
        iconClass: string;
    }

    export enum EAnomaly {
        Late = 0,
        DistancePointageDebut = 1,
        DistancePointageFin = 2,
        AbsenceLocalisationPointage = 3,
    }

    export const Anomaly: { [index: number]: IAnomaly } = {
        [EAnomaly.Late]: {
            id: EAnomaly.Late,
            name: "En retard",
            iconClass: "far fa-alarm-exclamation",
        },
        [EAnomaly.DistancePointageDebut]: {
            id: EAnomaly.DistancePointageDebut,
            name: "Distance pointage début intervention",
            iconClass: "far fa-map-marker-exclamation",
        },
        [EAnomaly.DistancePointageFin]: {
            id: EAnomaly.DistancePointageFin,
            name: "Distance pointage fin intervention",
            iconClass: "far fa-map-marker-exclamation",
        },
        [EAnomaly.AbsenceLocalisationPointage]: {
            id: EAnomaly.AbsenceLocalisationPointage,
            name: "Absence de localisation pointage",
            iconClass: "far fa-location-dot-slash",
        },
    };

    // Classes
    export class InterventionSecondaire {
        id?: number;
        description: string = "";
        status: boolean = false;
        client_id?: number;
        _client?: Contact;
        __uniqueid = _.uniqueId();

        constructor(data: any) {
            var self = this;
            _.assign(this, _.omit(data, ["client"]));
            if (data.client) {
                self._client = new Contact(data.client);
            }
        }

        //
        get client(): Contact | undefined {
            return this._client;
        }

        set client(value: Contact | undefined) {
            if (value) {
                this._client = value;
                this.client_id = value.id;
            } else {
                this._client = undefined;
                this.client_id = undefined;
            }
        }
    }

    export class Facturation {
        id?: number;
        nb_intervenants: number = 1;
        nb_heures_par_intervenant: number = 0;
        taux_horaire: number = 0;
        complement: number = 0;
        remise: number = 0;
        taux_tva: number = 20;
        date_facturation: string = moment().format("YYYY-MM-DD");
        status: EFacturationStatus = EFacturationStatus.FactureNonEmise;
        intervention_id?: number;
        copropriete_id?: number;

        constructor(data: any) {
            var self = this;
            _.assign(this, _.omit(data));
        }

        //
        get date_facturation_str(): string {
            return DateUtils.sqlToDateStr(this.date_facturation);
        }

        set date_facturation_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.date_facturation = newDate;
            }
        }

        //
        get nb_heures_total(): number {
            return (
                _.round(
                    this.nb_heures_par_intervenant * this.nb_intervenants,
                    2
                ) || 0
            );
        }

        get montant_ht_apres_remise(): number {
            return (
                _.round(
                    this.montant_ht - (this.montant_ht * this.remise) / 100,
                    2
                ) || 0
            );
        }

        get montant_tva(): number {
            return (
                _.round(
                    (this.montant_ht_apres_remise * this.taux_tva) / 100,
                    2
                ) || 0
            );
        }

        get montant_ht(): number {
            return _.round(
                this.nb_heures_total * this.taux_horaire + this.complement,
                2
            );
        }

        get montant_ttc(): number {
            return _.round(this.montant_ht_apres_remise + this.montant_tva, 2);
        }

        // Utils
        serialize(): Intervention.IFacturation {
            var res = {
                id: this.id,
                intervention_id: this.intervention_id,
                copropriete_id: this.copropriete_id,
                nb_intervenants: this.nb_intervenants,
                nb_heures_par_intervenant: this.nb_heures_par_intervenant,
                taux_horaire: this.taux_horaire,
                complement: this.complement,
                remise: this.remise,
                taux_tva: this.taux_tva,
                montant_ht: this.montant_ht,
                montant_ttc: this.montant_ttc,
                date_facturation: this.date_facturation,
                status: this.status,
            };

            return res;
        }
    }

    export class Notification {
        notification_number: string = "";
        content: string = "";
        recipient: string = "";
        created_at?: string;
        attachments: Array<any> = [];

        constructor(data: any) {
            var self = this;
            _.assign(this, _.omit(data));
        }
    }

    export enum EFacturationStatus {
        FactureNonEmise = 0,
        FactureEmise = 1,
    }

    //Alertes
    export enum EAlertType {
        LateInterventions = "late_interventions",
        LateFacturations = "late_facturations",
        OfflineUsers = "offline_users",
    }

    // UI
    export enum UIActionType {
        show_intervention,
        create_intervention,
        edit_intervention,
        delete_intervention,
        show_notification,
    }
}
